import React, { useState } from "react";
import {
  registerStep1Api,
  registerStep2Api,
  setPasswordApi,
  checkUsernameValidApi,
  changeUsernameApi,
  verifyGoogleApi,
  getRegisterTokenApi,
} from "../../services/authApi";
import {
  checkResponseData,
  checkErrorMessage,
} from "../../utils/responseHandler";
import { useToast, TOAST_TYPES } from "../../utils/toast";
import "./RegisterForm.css";
import "./LoginForm.css";
import { useGoogleLogin } from "@react-oauth/google";

const RegisterForm = ({ onSwitchToLogin }) => {
  const [step, setStep] = useState(1);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [confirmCode, setConfirmCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [username, setUsername] = useState("");
  const [usernameValid, setUsernameValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState(null);
  const [userData, setUserData] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const { showToast, ToastContainer } = useToast();

  const startCountdown = () => {
    setCountdown(60);
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendCode = async () => {
    if (!phoneNumber.trim()) {
      showToast("لطفا شماره تلفن را وارد کنید", TOAST_TYPES.WARNING);
      return;
    }

    setLoading(true);
    try {
      const response = await registerStep1Api(phoneNumber);
      const result = checkResponseData(response);

      if (result.success) {
        showToast(result.message || "کد تایید ارسال شد", TOAST_TYPES.SUCCESS);
        startCountdown();
        setStep(2);
      } else {
        showToast(result.message || "خطا در ارسال کد", TOAST_TYPES.ERROR);
      }
    } catch (error) {
      const errorDetails = checkErrorMessage(error);
      showToast(errorDetails.message, TOAST_TYPES.ERROR);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!confirmCode.trim()) {
      showToast("لطفا کد تایید را وارد کنید", TOAST_TYPES.WARNING);
      return;
    }

    setLoading(true);
    try {
      const response = await registerStep2Api(phoneNumber, confirmCode);
      const result = checkResponseData(response);

      if (result.success) {
        setToken(result.token);
        if (result.data?.data?.[0]) {
          setUserData(result.data.data[0]);
        }

        showToast(result.message || "تایید شد", TOAST_TYPES.SUCCESS);
        setStep(3);
      } else {
        showToast(result.message || "کد نامعتبر است", TOAST_TYPES.ERROR);
      }
    } catch (error) {
      const errorDetails = checkErrorMessage(error);
      showToast(errorDetails.message, TOAST_TYPES.ERROR);
    } finally {
      setLoading(false);
    }
  };

  const handleSetPassword = async () => {
    if (!password.trim() || !confirmPassword.trim()) {
      showToast("لطفا رمز عبور را وارد کنید", TOAST_TYPES.WARNING);
      return;
    }

    if (password !== confirmPassword) {
      showToast("رمز عبور و تکرار آن مطابقت ندارند", TOAST_TYPES.ERROR);
      return;
    }

    if (password.length < 6) {
      showToast("رمز عبور باید حداقل ۶ کاراکتر باشد", TOAST_TYPES.WARNING);
      return;
    }

    setLoading(true);
    try {
      // استفاده از setPasswordApi به جای registerStep3Api
      const response = await setPasswordApi(password, confirmPassword);
      const result = checkResponseData(response);

      if (result.success) {
        showToast(
          result.message || "رمز عبور با موفقیت ثبت شد",
          TOAST_TYPES.SUCCESS
        );
        setStep(4);
      } else {
        showToast(result.message || "خطا در ثبت رمز عبور", TOAST_TYPES.ERROR);
      }
    } catch (error) {
      const errorDetails = checkErrorMessage(error);
      showToast(errorDetails.message, TOAST_TYPES.ERROR);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckUsername = async () => {
    if (!username.trim()) {
      showToast("لطفا نام کاربری را وارد کنید", TOAST_TYPES.WARNING);
      return;
    }

    setLoading(true);
    try {
      const response = await checkUsernameValidApi(username);
      const result = checkResponseData(response);

      if (result.success) {
        setUsernameValid(true);
        showToast("نام کاربری قابل استفاده است", TOAST_TYPES.SUCCESS);
      } else {
        setUsernameValid(false);
        showToast(
          result.message || "این نام کاربری قبلاً استفاده شده",
          TOAST_TYPES.ERROR
        );
      }
    } catch (error) {
      const errorDetails = checkErrorMessage(error);
      showToast(errorDetails.message, TOAST_TYPES.ERROR);
    } finally {
      setLoading(false);
    }
  };

  const handleSetUsername = async () => {
    if (!usernameValid) {
      showToast(
        "لطفا ابتدا اعتبار نام کاربری را بررسی کنید",
        TOAST_TYPES.WARNING
      );
      return;
    }

    setLoading(true);
    try {
      const response = await changeUsernameApi(username);
      const result = checkResponseData(response);

      if (result.success) {
        showToast(
          result.message || "ثبت نام با موفقیت تکمیل شد",
          TOAST_TYPES.SUCCESS
        );
        setTimeout(() => window.location.reload(), 1500);
      } else {
        showToast(result.message || "خطا در ثبت نام کاربری", TOAST_TYPES.ERROR);
      }
    } catch (error) {
      const errorDetails = checkErrorMessage(error);
      showToast(errorDetails.message, TOAST_TYPES.ERROR);
    } finally {
      setLoading(false);
    }
  };

  const googleRegister = useGoogleLogin({
    flow: "auth-code",
    onSuccess: async (codeResponse) => {
      try {
        const res = await verifyGoogleApi(codeResponse.code, "register");
        const data = res.data;
        if (data.rtoken === "alreadyregistered") {
          showToast("شما قبلاً ثبت نام کرده‌اید. لطفاً وارد شوید.", TOAST_TYPES.WARNING);
          return;
        }
        const tokenRes = await getRegisterTokenApi(data.rtoken);
        if (tokenRes.data?.success) {
          showToast("ثبت نام با موفقیت انجام شد", TOAST_TYPES.SUCCESS);
          setTimeout(() => window.location.reload(), 1500);
        }
      } catch (error) {
        const errorDetails = checkErrorMessage(error);
        showToast(errorDetails.message, TOAST_TYPES.ERROR);
      }
    },
    onError: () => {
      showToast("خطا در ثبت نام با گوگل", TOAST_TYPES.ERROR);
    },
  });

  return (
    <>
      <ToastContainer />
      <div className="register-form">
        {/* استپ‌ها */}
        <div className="step-indicator">
          <div
            className={`step-dot ${step >= 1 ? "active" : ""} ${
              step > 1 ? "completed" : ""
            }`}
          ></div>
          <div
            className={`step-dot ${step >= 2 ? "active" : ""} ${
              step > 2 ? "completed" : ""
            }`}
          ></div>
          <div
            className={`step-dot ${step >= 3 ? "active" : ""} ${
              step > 3 ? "completed" : ""
            }`}
          ></div>
          <div className={`step-dot ${step >= 4 ? "active" : ""}`}></div>
        </div>

        {/* مرحله 1: شماره تلفن */}
        {step === 1 && (
          <div className="step-content">
            <input
              type="tel"
              className="auth-input"
              placeholder="شماره تلفن همراه (مثال: 09123456789)"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              dir="ltr"
            />

            <button
              onClick={handleSendCode}
              disabled={loading}
              className="auth-button"
            >
              {loading ? (
                <span className="button-content">
                  <span className="loading-spinner"></span>
                  در حال ارسال...
                </span>
              ) : (
                "دریافت کد تایید"
              )}
            </button>

            <div className="divider">
              <span>یا</span>
            </div>

            <button onClick={() => googleRegister()} className="google-button">
              <svg width="20" height="20" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              ثبت نام با گوگل
            </button>
          </div>
        )}

        {/* مرحله 2: کد تایید */}
        {step === 2 && (
          <div className="step-content">
            <p className="helper-text">
              کد تایید به شماره {phoneNumber} ارسال شد
            </p>

            <input
              type="text"
              className="auth-input"
              placeholder="کد تایید ۶ رقمی"
              value={confirmCode}
              onChange={(e) => setConfirmCode(e.target.value)}
              maxLength="6"
              dir="ltr"
            />

            <button
              onClick={handleVerifyCode}
              disabled={loading}
              className="auth-button"
            >
              {loading ? (
                <span className="button-content">
                  <span className="loading-spinner"></span>
                  در حال بررسی...
                </span>
              ) : (
                "تایید کد"
              )}
            </button>

            {countdown > 0 ? (
              <p className="countdown">ارسال مجدد تا {countdown} ثانیه دیگر</p>
            ) : (
              <button
                onClick={handleSendCode}
                className="auth-link"
                style={{ marginTop: "1rem" }}
              >
                ارسال مجدد کد
              </button>
            )}
          </div>
        )}

        {/* مرحله 3: رمز عبور */}
        {step === 3 && (
          <div className="step-content">
            <div className="password-input-container">
              <input
                type={showPassword ? "text" : "password"}
                className="auth-input password-input"
                placeholder="رمز عبور (حداقل ۶ کاراکتر)"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? "👁️" : "👁️‍🗨️"}
              </button>
            </div>

            <div className="password-input-container">
              <input
                type={showConfirmPassword ? "text" : "password"}
                className="auth-input password-input"
                placeholder="تکرار رمز عبور"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? "👁️" : "👁️‍🗨️"}
              </button>
            </div>

            <button
              onClick={handleSetPassword}
              disabled={loading}
              className="auth-button"
            >
              {loading ? (
                <span className="button-content">
                  <span className="loading-spinner"></span>
                  در حال ثبت...
                </span>
              ) : (
                "مرحله بعد"
              )}
            </button>
          </div>
        )}

        {/* مرحله 4: نام کاربری */}
        {step === 4 && (
          <div className="step-content">
            <div className="input-group">
              <input
                type="text"
                className="auth-input"
                placeholder="نام کاربری دلخواه"
                value={username}
                onChange={(e) => {
                  setUsername(e.target.value);
                  setUsernameValid(false);
                }}
              />
              <button
                onClick={handleCheckUsername}
                disabled={loading || !username.trim()}
                className="input-group-button"
              >
                {loading ? "..." : "بررسی"}
              </button>
            </div>

            <button
              onClick={handleSetUsername}
              disabled={loading || !usernameValid}
              className="auth-button"
            >
              {loading ? (
                <span className="button-content">
                  <span className="loading-spinner"></span>
                  در حال ثبت...
                </span>
              ) : (
                "تکمیل ثبت نام"
              )}
            </button>
          </div>
        )}

        <div className="text-center mt-4">
          <span style={{ color: "#8a9bb5" }}>قبلاً ثبت نام کرده‌اید؟ </span>
          <button onClick={onSwitchToLogin} className="auth-link">
            وارد شوید
          </button>
        </div>

        <div className="register-footer-links">
          <a
            href="https://app.umlang.com/privacypolicy"
            target="_blank"
            rel="noopener noreferrer"
            className="footer-link"
          >
            🔒 حریم خصوصی
          </a>
          <span className="footer-link-separator">|</span>
          <a
            href="https://app.umlang.com/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="footer-link"
          >
            📄 شرایط استفاده
          </a>
        </div>
      </div>

    </>
  );
};

export default RegisterForm;
